/**
 * Type definitions for Agent Builder
 * Used for visually configuring and saving AI agents
 */

// =============================================================================
// AGENT CONFIGURATION
// =============================================================================

/**
 * Agent SDK/Backend options
 */
export type AgentSDK = 'openai_agents' | 'eureka_agent' | 'langgraph';

/**
 * LLM provider options
 */
export type LLMProvider = 'azure_openai' | 'openai' | 'anthropic';

/**
 * Reasoning effort levels
 */
export type ReasoningEffort = 'minimal' | 'low' | 'medium' | 'high';

/**
 * LLM configuration for agents
 */
export interface LLMConfig {
  model: string;
  provider: LLMProvider;
  temperature?: number;
  max_tokens?: number;
  reasoning_effort?: ReasoningEffort;
  /** Resource ID from WFM - used for inline agent execution bindings */
  res_id?: string;
}

/**
 * Selected tool configuration for agent
 * Matches the tools section in agent.yaml
 */
export interface SelectedToolConfig {
  /** Tool name in format: mcp__<handle>__<tool_name> */
  name: string;
  /** Original MCP tool name (without prefix) */
  mcp_tool_name: string;
  /** Description (can override MCP tool description) */
  description: string;
  /** Whether the tool runs asynchronously */
  async?: boolean;
  /** Tool-specific timeout in seconds */
  timeout?: number;
  /** Permission requirement for dangerous operations */
  requires_permission?: {
    message: string;
  };
}

/**
 * MCP server binding configuration
 *
 * The `handle` is a user-defined unique name used as the lookup key in agent.yaml
 * and runtime_bindings. The same MCP server can be added multiple times with
 * different handles (e.g., "prod_db" and "staging_db" both pointing to same server type).
 *
 * agent.yaml declares:
 *   mcp_servers:
 *     - name: <handle>
 *       resource_type: <type>
 *       required: true
 *
 * runtime_bindings provides:
 *   resource_bindings:
 *     <handle>: <res_id>
 */
export interface MCPServerBinding {
  /** User-defined unique handle/name - used as lookup key in bindings */
  handle: string;
  /** Original MCP server name from the catalog */
  serverName: string;
  /** Full resource type string (e.g., "resources.mcp_server.MCPServerSSE.Tavily_Search_ETE") */
  type: string;
  /** Functional template name extracted from type (e.g., "Tavily_Search_ETE") - goes into agent.yaml */
  type_template?: string;
  /** Resource ID - the actual UUID of the MCP resource in WFM - goes into resource_bindings */
  res_id?: string;
  timeout?: number;
  /** Selected tools from this server (if undefined, all tools are selected) */
  selected_tools?: SelectedToolConfig[];
}

/**
 * Sub-agent binding configuration
 *
 * Mirrors MCP server pattern:
 *   agent_id (template)     → goes in agent.yaml `tools[].agent:`
 *   deployment_id (instance) → goes in `resource_bindings[tool_name]`
 */
export interface SubAgentBinding {
  /** Published agent ID (the template/definition) - goes into agent.yaml */
  agent_id: string;
  /** Deployment ID (running instance of the agent) - goes into resource_bindings */
  deployment_id?: string;
  /** Display name */
  name: string;
  description?: string;
  handoff_description?: string;
}

/**
 * Agent configuration - matches WFM meta_data structure
 */
export interface AgentConfig {
  // Identity
  name: string;
  description: string;
  version: string;

  // Execution
  sdk: AgentSDK;
  llm: LLMConfig;
  instructions: string;

  // Tools & integrations
  tools?: string[];
  mcp_servers?: MCPServerBinding[];
  sub_agents?: SubAgentBinding[];

  // Schema definitions
  input_schema?: unknown[];
  output_schema?: unknown[];

  // Display info
  capabilities?: string[];
  usage_examples?: string[];

  // ProCode specific (for eureka_agent SDK)
  venv_blob_url?: string;
  entry_point?: string;

  // File-based specific
  base_path?: string;
  agent_file?: string;
}

/**
 * Default agent configuration
 */
export const DEFAULT_AGENT_CONFIG: AgentConfig = {
  name: '',
  description: '',
  version: '1.0.0',
  sdk: 'openai_agents',
  llm: {
    model: 'gpt-4o',
    provider: 'azure_openai',
    temperature: 0.7,
  },
  instructions: '',
  mcp_servers: [],
  sub_agents: [],
  capabilities: [],
  usage_examples: [],
};

// =============================================================================
// MCP GATEWAY TYPES
// =============================================================================

/**
 * MCP Server scope
 */
export type MCPServerScope = 'project' | 'tenant' | 'global' | 'user';

/**
 * MCP server list item from GET /mcp/list
 *
 * Response from WFM resources API filtered by MCP server types.
 */
export interface MCPServerListItem {
  /** Resource ID (UUID) */
  id: string;
  /** Server name */
  name: string;
  /** Display name (human-friendly) */
  display_name?: string;
  /** Resource type (e.g., "resources.mcp_server.MCPServerSSE.Tavily_Search_ETE") */
  type: string;
  /** Functional template name extracted from type (e.g., "Tavily_Search_ETE") */
  type_template?: string;
  /** Scope of the resource */
  scope: MCPServerScope;
  /** Scope ID (e.g., project ID) */
  scope_id?: string;
  /** Tags */
  tags?: string[];
  /** Optional description */
  description?: string;
  /** Status (for UI display) */
  status?: 'available' | 'unavailable' | 'initializing';
}

/**
 * MCP tool definition
 */
export interface MCPTool {
  name: string;
  title?: string;
  description: string;
  inputSchema: {
    type: 'object';
    properties: Record<string, MCPToolParameter>;
    required?: string[];
  };
}

/**
 * MCP tool parameter
 */
export interface MCPToolParameter {
  type: string;
  description?: string;
  enum?: string[];
  default?: unknown;
  items?: Record<string, unknown>;
}

/**
 * MCP resource definition
 */
export interface MCPResource {
  uri: string;
  name: string;
  description?: string;
  mimeType?: string;
}

/**
 * MCP session response from initialize
 */
export interface MCPSessionResponse {
  /** Session ID for subsequent requests (from Mcp-Session-Id header) */
  sessionId: string;
  /** Resource ID used to create this session */
  resourceId?: string;
  /** Server information from MCP */
  serverInfo?: {
    name: string;
    version: string;
  };
  /** Server capabilities */
  capabilities?: {
    tools?: boolean;
    resources?: boolean;
    prompts?: boolean;
  };
}

// =============================================================================
// WFM (WORKFLOW MANAGER) TYPES
// =============================================================================

/**
 * Agent status
 */
export type WFMAgentStatus = 'draft' | 'published' | 'deprecated' | 'archived';

/**
 * WFM Agent from backend
 */
export interface WFMAgent {
  agent_id: string;
  workflow_id?: string;
  name: string;
  description?: string;
  version?: string;
  status?: WFMAgentStatus;
  meta_data: Record<string, unknown>;
  created_at: string;
  updated_at?: string;
  created_by?: string;
}

/**
 * Create FlowsAlpha agent request
 */
export interface CreateFlowsAlphaAgentRequest {
  name: string;
  description: string;
  version: string;
  workflow_variant: AgentSDK;
  meta_data: {
    sdk: AgentSDK;
    llm: LLMConfig;
    instructions: string;
    tools?: string[];
    mcp_servers?: MCPServerBinding[];
    sub_agents?: SubAgentBinding[];
    input_schema?: unknown[];
    output_schema?: unknown[];
    capabilities?: string[];
    usage_examples?: string[];
    venv_blob_url?: string;
    entry_point?: string;
    base_path?: string;
    agent_file?: string;
  };
}

/**
 * Create agent response
 */
export interface CreateAgentResponse {
  workflow_id: string;
  agent_id: string;
  version: string;
  name: string;
}

// =============================================================================
// AGENT BUILDER PAGE TYPES
// =============================================================================

/**
 * Builder tab options
 */
export type BuilderTab = 'config' | 'mcp' | 'subagents';

/**
 * Editor mode for the builder
 * - 'edit': Full-screen tabs for configuration
 * - 'chat': Read-only summary panel + chat panel
 */
export type EditorMode = 'edit' | 'chat';

/**
 * Form action for resource forms
 */
export type FormAction = 'create' | 'edit' | 'view';

/**
 * Available LLM models by provider
 */
export const LLM_MODELS: Record<LLMProvider, { value: string; label: string }[]> = {
  azure_openai: [
    { value: 'gpt-4o', label: 'GPT-4o' },
    { value: 'gpt-4o-mini', label: 'GPT-4o Mini' },
    { value: 'gpt-4-turbo', label: 'GPT-4 Turbo' },
    { value: 'o1-preview', label: 'o1 Preview' },
    { value: 'o1-mini', label: 'o1 Mini' },
  ],
  openai: [
    { value: 'gpt-4o', label: 'GPT-4o' },
    { value: 'gpt-4o-mini', label: 'GPT-4o Mini' },
    { value: 'gpt-4-turbo', label: 'GPT-4 Turbo' },
    { value: 'o1-preview', label: 'o1 Preview' },
    { value: 'o1-mini', label: 'o1 Mini' },
  ],
  anthropic: [
    { value: 'claude-sonnet-4-20250514', label: 'Claude 4 Sonnet' },
    { value: 'claude-3-5-sonnet-20241022', label: 'Claude 3.5 Sonnet' },
    { value: 'claude-3-5-haiku-20241022', label: 'Claude 3.5 Haiku' },
    { value: 'claude-3-opus-20240229', label: 'Claude 3 Opus' },
  ],
};

/**
 * SDK options for display
 */
export const SDK_OPTIONS: { value: AgentSDK; label: string; description: string }[] = [
  {
    value: 'openai_agents',
    label: 'OpenAI Agents SDK',
    description: 'Use OpenAI\'s agent framework with function calling',
  },
  {
    value: 'eureka_agent',
    label: 'Eureka Agent',
    description: 'Custom Python agent with full control',
  },
  {
    value: 'langgraph',
    label: 'LangGraph',
    description: 'Graph-based agent orchestration',
  },
];

/**
 * Provider options for display
 */
export const PROVIDER_OPTIONS: { value: LLMProvider; label: string }[] = [
  { value: 'azure_openai', label: 'Azure OpenAI' },
  { value: 'openai', label: 'OpenAI' },
  { value: 'anthropic', label: 'Anthropic' },
];

/**
 * Reasoning effort options
 */
export const REASONING_OPTIONS: { value: ReasoningEffort; label: string }[] = [
  { value: 'minimal', label: 'Minimal' },
  { value: 'low', label: 'Low' },
  { value: 'medium', label: 'Medium' },
  { value: 'high', label: 'High' },
];

// =============================================================================
// BUILDER STATE MACHINE TYPES
// =============================================================================

/**
 * Builder mode representing the persistence state
 */
export type BuilderMode = 'new' | 'draft' | 'published';

/**
 * Builder view - home screen or editor
 */
export type BuilderView = 'home' | 'editor';

/**
 * WFM Workflow (draft or published)
 */
export interface WFMWorkflow {
  workflow_id: string;
  name: string;
  description?: string;
  is_draft: boolean;
  published: boolean;
  workflow_type: string;
  workflow_variant?: string;
  version?: string;
  meta_data: Record<string, unknown>;
  created_at: string;
  modified_at: string;
}

/**
 * Resource for binding (LLM, storage, etc.)
 */
export interface Resource {
  id: string;
  name: string;
  type: string;
  description?: string;
}

/**
 * Complete builder state
 */
export interface AgentBuilderState {
  // View state
  view: BuilderView;

  // Persistence state
  mode: BuilderMode;
  workflowId?: string;
  agentId?: string;
  version?: string;
  baseVersion?: string; // When editing published agent, tracks original version

  // Config state
  config: AgentConfig;

  // Auto-save state
  isDirty: boolean;
  lastSavedAt?: Date;

  // UI state
  activeTab: BuilderTab;
  editorMode: EditorMode;
  isSaving: boolean;
  isTesting: boolean;
  isPublishing: boolean;
  isLoading: boolean;

  // Error state
  error?: string;
}

/**
 * Default builder state
 */
export const DEFAULT_BUILDER_STATE: AgentBuilderState = {
  view: 'home',
  mode: 'new',
  config: DEFAULT_AGENT_CONFIG,
  isDirty: false,
  activeTab: 'config',
  editorMode: 'edit',
  isSaving: false,
  isTesting: false,
  isPublishing: false,
  isLoading: false,
};

/**
 * Props for AgentBuilderPage
 */
export interface AgentBuilderPageProps {
  /** Project ID (required) */
  projectId: string;
  /** User email (required) */
  userEmail: string;
  /** Tenant ID for API calls */
  tenantId?: string;
  /** Optional: Load existing draft by workflow_id */
  initialWorkflowId?: string;
  /** Optional: Load published agent for new version */
  initialAgentId?: string;
  /** Callback when agent is published */
  onPublish?: (result: { workflow_id: string; agent_id: string; version: string }) => void;
  /** Callback when user exits builder */
  onExit?: () => void;
}

// =============================================================================
// INLINE EXECUTION / RUN
// =============================================================================

/** Streaming execution event from WebSocket */
export interface ExecutionStreamEvent {
  type: string;
  data: Record<string, unknown>;
  timestamp: number;
}

/** Run panel state machine */
export type RunState =
  | { status: 'idle' }
  | { status: 'starting' }
  | { status: 'running'; executionId: string; events: ExecutionStreamEvent[] }
  | { status: 'completed'; executionId: string; events: ExecutionStreamEvent[]; result?: string }
  | { status: 'error'; executionId: string; events: ExecutionStreamEvent[]; error: string };
